import { memo, PropsWithChildren, ReactNode, useContext } from 'react';
import { DropZone as RACDropZone, DropZoneProps as RACDropZoneProps, FileTrigger } from 'react-aria-components';

import DropZoneIcon from '@/assets/drop-zone-icon.svg?react';
import { merge } from '@/components';
import { Button, DropZoneContext, Flex, Text } from '@/components/core';
import { formatFileSize } from '@/utils';

export const DropZoneButton = ({ children }: PropsWithChildren) => {
  const { acceptedMimeTypes, allowsMultiple, maxFileSize, onDropFiles } = useContext(DropZoneContext);

  return (
    <FileTrigger
      allowsMultiple={allowsMultiple}
      acceptedFileTypes={acceptedMimeTypes}
      onSelect={(e) => {
        if (e) {
          let files = Array.from(e);
          if (acceptedMimeTypes) {
            files = files.filter((file) => acceptedMimeTypes.includes(file.type));
          }
          if (maxFileSize) {
            files = files.filter((file) => file.size <= maxFileSize);
          }
          onDropFiles?.(files);
        }
      }}
    >
      <Button className="hover:bg-primary-25 h-full w-full" variant="plain" data-testid="upload-files-button">
        {children ?? (
          <Flex col className="h-full w-full items-center justify-center">
            <DropZoneIcon className="size-16" />
            <Flex className="gap-xs">
              <Text>Click to upload</Text>
              <Text className="!font-regular">or drag and drop</Text>
            </Flex>
            <Text className="!font-regular !text-xs !text-gray-500">
              ({acceptedMimeTypes ? acceptedMimeTypes.map((mimeType) => mimeType.split('/')[1]).join(', ') : null}
              {maxFileSize ? `. Max size: ${formatFileSize(maxFileSize)}` : null})
            </Text>
          </Flex>
        )}
      </Button>
    </FileTrigger>
  );
};

export type DropZoneProps = Omit<RACDropZoneProps, 'children'> & {
  acceptedMimeTypes?: string[];
  allowsMultiple?: boolean;
  maxFileSize?: number;
  onDropFiles?: (files: File[]) => void;
  children?: ReactNode;
};

const IDropZone = ({
  acceptedMimeTypes,
  allowsMultiple,
  maxFileSize,
  onDropFiles,
  className,
  children,
  ...rest
}: DropZoneProps) => {
  return (
    <RACDropZone
      className={merge(
        'border-primary-100 flex flex-col items-center justify-center rounded-lg border-1 border-dashed',
        className
      )}
      getDropOperation={(types) => {
        if (acceptedMimeTypes) {
          let hasSomeValidFiles = false;
          for (const acceptedMimeType of acceptedMimeTypes) {
            if (types.has(acceptedMimeType)) {
              hasSomeValidFiles = true;
            }
          }
          return hasSomeValidFiles ? 'copy' : 'cancel';
        }
        return 'copy';
      }}
      onDrop={async (e) => {
        let fileDropItems = e.items.filter((di) => di.kind === 'file');
        if (acceptedMimeTypes) {
          fileDropItems = fileDropItems.filter((fdi) => acceptedMimeTypes.includes(fdi.type));
        }
        let files = await Promise.all(fileDropItems.map((fdi) => fdi.getFile()));
        if (maxFileSize) {
          files = files.filter((file) => file.size <= maxFileSize);
        }
        onDropFiles?.(files);
      }}
      {...rest}
    >
      <DropZoneContext value={{ maxFileSize, onDropFiles, acceptedMimeTypes, allowsMultiple }}>
        {children}
      </DropZoneContext>
    </RACDropZone>
  );
};

export const DropZone = memo(IDropZone) as typeof IDropZone;
